import { JSX, useContext, useState } from "react";
import { toast } from "react-toastify";
import { FlyoutContext, FlyoutFooter } from "@app/shared/Flyouts";
import { QESButton } from "@app/shared/qes-standards/Button";
import { BulkTargetExistingTargetActionEnum } from "@app/targeting/import/interfaces";

interface TargetingWizardFlyoutProps {
  onSubmit: () => Promise<boolean>;
  targetAction: BulkTargetExistingTargetActionEnum;
}

const TargetingWizardFlyout = (props: TargetingWizardFlyoutProps): JSX.Element => {
  const [submitting, setSubmitting] = useState(false);
  const flyoutContext = useContext(FlyoutContext);

  const errorMessage = () => {
    toast("Something went wrong. Please try again later.",
      {
        containerId: "z-index-9999",
        type: "error",
      });
    setSubmitting(false);
  };

  const handleSubmit = () => {
    if (submitting) {
      return;
    }
    setSubmitting(true);
    props.onSubmit().then((result: boolean) => {
      if (result) {
        toast("Your targets are being processed.",
          {
            containerId: "z-index-9999",
            type: "success",
          });
        flyoutContext.handleCloseFlyout();
      } else {
        errorMessage();
      }
    },
      () => { errorMessage(); });
  };

  return (
    <>
      <div className="targeting-wizard-flyout">
        <p className="lead"><i className="fa fa-exclamation-triangle d-inline-block me-3" />Are you sure?</p>
        <hr />
        <div>
          <p>While the system is targeting providers the projects you selected will no longer be able to:</p>
          <ul>
            <li>Make changes</li>
            <li>Make updates</li>
            <li>Generate additional targeting providers</li>
          </ul>
          <p>Any project with existing targeted providers will be {props.targetAction === BulkTargetExistingTargetActionEnum.Replace ? <span>overwritten</span> : <span>appended to, and not overwritten. Any uploaded providers that are currently <b>excluded</b>, will not be targeted, or have history updated</span>}.</p>
          {props.targetAction === BulkTargetExistingTargetActionEnum.Replace &&
            <p className="demibold">Caution: This action will only target the providers you uploaded on your file. You will be removing all previously-targeted providers and deleting the targeted provider history in the projects you selected.</p>}
          <p className="demibold">Once the targeting has been started there will be no way to cancel it.</p>
        </div>
      </div>
      <FlyoutFooter
        submitButton={
          <QESButton qesType="primary" className="ms-3" onClick={handleSubmit} disabled={submitting} qesTestId="import-targeting__flyout-run">
            {submitting ? (
              <div className="spinner-border spinner-border-sm" role="status">
                <span className="visually-hidden">Loading...</span>
              </div>
            ) : "Run Now"}
          </QESButton>
        }
      />
    </>
  );
};

export default TargetingWizardFlyout;
